import { OptionalLink, OptionalLinkProps } from '../01-core/OptionalLink';
import { Button, ButtonProps } from '../02-atoms/Button';
import { Media, MediaProps } from '../02-atoms/Media';

const TITLE_PREFIX = /^(Fachhochschule|University of|Hochschule für|School of)/;

export interface FooterLogoProps extends OptionalLinkProps {
  /** The logo */
  media: MediaProps;
}

export interface LanguageItemProps extends OptionalLinkProps {
  /** The label */
  label: string;
  /** Whether the item is active */
  active?: boolean;
}

export interface NavigationItemProps extends OptionalLinkProps {
  /** The label */
  label: string;
}

export interface FooterNavigationProps extends NavigationItemProps {
  /** The navigation items */
  items?: NavigationItemProps[];
}

export interface FooterProps {
  /** The title */
  title: string;
  /** The sub-title */
  subtitle?: string;
  /** To top title */
  totop?: string;
  /** CTA text */
  cta_text?: string;
  /** CTA button */
  cta_button?: ButtonProps;
  /** List of navigations with items */
  items?: FooterNavigationProps[];
  /** Logos */
  logos?: FooterLogoProps[];
  /** Colophon */
  colophon?: string;
  /** Colophon navigation items */
  colophon_items?: NavigationItemProps[];
  /** Languages */
  languages?: LanguageItemProps[];
}

/** Block rendering the footer */
export const Footer = ({
  title,
  subtitle,
  totop,
  cta_text,
  cta_button,
  items,
  logos,
  colophon,
  colophon_items,
  languages,
}: FooterProps) => {
  const scrollToTop = (e: React.MouseEvent<HTMLButtonElement> | MouseEvent) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (e instanceof HTMLElement) e.blur();
  };

  const title_prefix = TITLE_PREFIX.exec(title)?.[0];
  if (title_prefix) {
    title = title.replace(TITLE_PREFIX, '').trim();
  }

  return (
    <footer className="footer">
      <div className="footer__top">
        {totop && (
          <div className="footer__totop">
            <Button
              type="primary"
              icon="arrow_up_triangle"
              title={totop}
              onClick={scrollToTop}
            />
          </div>
        )}
        {title && (
          <p className="footer__title">
            {title_prefix && (
              <>
                {title_prefix}
                <br />
              </>
            )}
            {title}{' '}
            {subtitle && <span className="footer__subtitle">{subtitle}</span>}
          </p>
        )}
      </div>
      <div className="footer__content">
        {(cta_text || cta_button) && (
          <div className="footer__cta">
            {cta_text && <p>{cta_text}</p>}
            {cta_button && <Button {...cta_button} />}
          </div>
        )}
        {items && (
          <nav className="footer__nav">
            <ul className="footer__nav-items">
              {items.map((item, i) => {
                return (
                  <li key={i}>
                    <strong>
                      <OptionalLink span={true} {...item}>
                        {item.label}
                      </OptionalLink>
                    </strong>
                    {item.items && (
                      <ul className="footer__nav-subitems">
                        {item.items.map((item, i) => {
                          return (
                            <li key={i}>
                              <OptionalLink span={true} {...item}>
                                {item.label}
                              </OptionalLink>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          </nav>
        )}
        {logos && (
          <div className="footer__logos">
            {logos.map((logo, i) => {
              return (
                <OptionalLink div={true} key={i} {...logo}>
                  <Media {...logo.media} />
                </OptionalLink>
              );
            })}
          </div>
        )}
      </div>
      <div className="footer__colophon">
        {colophon}
        {colophon_items && (
          <ul className="footer__colophon-items">
            {colophon_items.map((item, i) => {
              return (
                <li key={i}>
                  <OptionalLink span={true} {...item}>
                    {item.label}
                  </OptionalLink>
                </li>
              );
            })}
          </ul>
        )}
        {languages && (
          <ul className="footer__languages">
            {languages.map((item, i) => {
              return (
                <li key={i}>
                  <OptionalLink
                    className={item.active ? 'footer__active-language' : ''}
                    button={true}
                    {...item}
                  >
                    {item.label}
                  </OptionalLink>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </footer>
  );
};
